import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

// Rows for the "Export Excel" button on the Kunder page: every field the card
// stores (Info tab + billing settings), plus the records linked to it from
// other collections, joined client-side:
//   contacts      (contacts.kundekortIds)       -> one row per contact; the
//                                                  customer's own columns are
//                                                  filled on its first row only
//   system-kunder (links to system_info)        -> "Systems"
//   groups        (groups.customerId)           -> "Groups (billed)"
//   transactions  (row._sales, from the list)   -> "Sales YYYY-MM (inc. VAT)"
// Column order here is the column order in the sheet.

const fmtDate = (v: any): string => {
  if (!v) { return ''; }
  if (typeof v.toDate === 'function') { v = v.toDate(); }   // Firestore Timestamp
  if (typeof v === 'number' && v < 1e12) { v = v * 1000; }  // unix seconds (createdOn/updatedOn)
  const m = moment(v);
  return m.isValid() ? m.format('YYYY-MM-DD') : String(v);
};

const yesNo = (v: boolean) => v ? 'Yes' : 'No';

// Stored as a decimal (0.15) but older cards may hold a whole percent (15).
const pct = (v: any): string => {
  if (v == null || v === '') { return ''; }
  const n = Number(v);
  if (!isFinite(n)) { return ''; }
  return `${n > 1 ? n : n * 100}%`;
};

const contactColumns = (c: any) => ({
  'Contact name': c?.name || '',
  'Contact title': c?.stilling || '',
  'Contact email': c?.email || '',
  'Contact phone': c?.phone || '',
  'Contact type': Array.isArray(c?.roles) ? c.roles.join(', ') : '',
});

export function buildKunderExportRows(
  customers: any[],
  contacts: any[],
  systems: any[],
  systemLinks: any[],
  groups: any[],
  salesMonth: string
): any[] {
  const contactsByCustomer: { [id: string]: any[] } = {};
  // A contact can be linked to several cards (contact book), with roles per card.
  (contacts || []).forEach(c => {
    const ids: string[] = c.kundekortIds || (c.kundekortId ? [c.kundekortId] : []);
    ids.forEach(id => {
      const roles = c.rolesByKundekort ? (c.rolesByKundekort[id] || []) : c.roles;
      (contactsByCustomer[id] = contactsByCustomer[id] || []).push({ ...c, roles });
    });
  });

  // A link's systemId may be the system_info doc id OR its systemId field.
  const systemByAnyId: { [id: string]: any } = {};
  (systems || []).forEach(s => {
    if (s.systemId != null) { systemByAnyId[String(s.systemId)] = s; }
    if (s.dbid) { systemByAnyId[s.dbid] = s; }
  });
  const systemsByCustomer: { [id: string]: string[] } = {};
  (systemLinks || []).forEach(l => {
    if (!l.kundekortId) { return; }
    const s = systemByAnyId[String(l.systemId)];
    const label = s ? `${s.number != null ? s.number + ' ' : ''}${s.name || s.systemId || ''}`.trim() : String(l.systemId || '');
    (systemsByCustomer[l.kundekortId] = systemsByCustomer[l.kundekortId] || []).push(label);
  });

  const groupsByCustomer: { [id: string]: string[] } = {};
  (groups || []).forEach(g => {
    if (!g.customerId) { return; }
    (groupsByCustomer[g.customerId] = groupsByCustomer[g.customerId] || []).push(g.name || g.groupId || '');
  });

  const rows: any[] = [];
  (customers || []).forEach(c => {
    const id = c.kundekortId;
    const customer: any = {
      'Name': c.name || '',
      'Legal name': c.legalName || '',
      'Part of': c._parentName || '',
      'Customer type': c.customerType || '',
      'Category': c.category || '',
      'Org no': c.orgId || '',
      'PowerOffice customer no': c.powerOfficeCustomerNo || '',
      'Email': c.email || '',
      'Phone': c.phone || '',
      'Website': c.web || '',
      'Address': c.address || '',
      'Address 2': c.address2 || '',
      'Zip code': c.zipCode || '',
      'City': c.city || '',
      'Country': c.country || '',
      'Lead source': c.leadSource || '',
      'Owner': c.ownerName || '',
      'Our reference': c.ourReference || '',
      'Customer reference': c.customerReference || '',
      'VAT rate': pct(c.vatRate != null ? c.vatRate : 0.15),
      'Invoice lines by group': yesNo(c.invoiceLinesByGroup === true),
      'Kickback rate': pct(c.kickbackRate),
      'Driftskostnader': yesNo(c.driftkostnaderEnabled !== false),
      'Systems': (systemsByCustomer[id] || []).join('; '),
      'Groups (billed)': (groupsByCustomer[id] || []).join('; '),
      // Frigg-app sales on the card's own systems (not its units'), inc. VAT.
      [`Sales ${salesMonth} (inc. VAT)`]: c._sales != null ? c._sales : '',
      'Created': fmtDate(c.createdOn),
      'Updated': fmtDate(c.updatedOn),
      'Kundekort ID': id || ''
    };
    const { Name, ...rest } = customer;
    const contacts = contactsByCustomer[id] || [];
    // Contact columns sit right after Name so each contact reads on its own line.
    (contacts.length ? contacts : [null]).forEach((ct, i) => {
      if (i === 0) {
        rows.push({ 'Name': Name, ...contactColumns(ct), ...rest });
      } else {
        const blank: any = { 'Name': '', ...contactColumns(ct) };
        Object.keys(rest).forEach(k => blank[k] = '');
        rows.push(blank);
      }
    });
  });
  return rows;
}
